import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { oapi, ymd } from '@/lib/officialsApi';
import { AvailabilityRequests } from './AvailabilityRequests';

const CELL = {
  available: 'bg-emerald-500 text-white',
  unavailable: 'bg-red-500 text-white',
  partial: 'bg-amber-300 text-amber-950',
  unknown: 'bg-white text-slate-500 border border-dashed border-slate-200',
};
const LABEL = { available: 'Available', unavailable: 'Unavailable', partial: 'Partly blocked', unknown: 'No info' };

const cellTitle = (c) => [LABEL[c.status], c.ranges?.length ? `Available ${c.ranges.join(', ')}` : '',
  c.blocked?.length ? `Blocked ${c.blocked.join(', ')}` : '', c.assignments ? `${c.assignments} game(s) assigned` : ''].filter(Boolean).join(' · ');

const Legend = () => (
  <div className="flex flex-wrap gap-4 text-[11px] text-slate-500">
    {Object.entries(LABEL).map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${CELL[k]}`} />{l}</span>)}
    <span className="flex items-center gap-1.5"><span className="font-black text-slate-700">2</span>= games already assigned</span>
  </div>
);

const Grid = ({ data, rows }) => (
  <div className="overflow-x-auto" data-testid="availability-grid">
    <table className="min-w-full text-xs border-separate border-spacing-0">
      <thead>
        <tr>
          <th className="sticky left-0 bg-white z-10 text-left px-3 py-2 font-bold text-slate-500 min-w-[170px]">Official</th>
          {data.dates.map(d => <th key={d.date} className="px-0.5 py-2 font-bold text-slate-500 text-center min-w-[42px] whitespace-nowrap">{d.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(o => (
          <tr key={o.id} data-testid={`avail-row-${o.id}`}>
            <td className="sticky left-0 bg-white z-10 px-3 py-1.5 border-t">
              <div className="font-semibold text-slate-800 truncate max-w-[180px]">{o.full_name}</div>
              <div className="text-[10px] text-slate-400">{!o.has_account ? 'No account yet' : o.updated_at ? `Updated ${new Date(o.updated_at).toLocaleDateString()}` : 'Not submitted'}</div>
            </td>
            {o.days.map(c => (
              <td key={c.date} className="border-t px-0.5 py-1">
                <div title={cellTitle(c)} className={`h-8 rounded-md flex items-center justify-center font-black ${CELL[c.status] || CELL.unknown}`} data-testid={`avail-cell-${o.id}-${c.date}`}>
                  {c.assignments || ''}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No officials yet.</p>}
  </div>
);

export const AvailabilityTab = ({ orgId, manage, officials }) => {
  const [start, setStart] = useState(() => ymd(new Date()));
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    setData(null);
    oapi('/officials/availability-overview', { orgId, query: { start, days: 14 } }).then(setData).catch(e => toast.error(e.message));
  }, [orgId, start]);
  const shift = (n) => { const [y, m, d] = start.split('-').map(Number); setStart(ymd(new Date(y, m - 1, d + n))); };
  const rows = (data?.officials || []).filter(o => !q || o.full_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="officials-availability-tab">
      {manage && <AvailabilityRequests orgId={orgId} officials={officials} />}
      <section className="bg-white border rounded-2xl">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b">
          <h3 className="text-lg font-black uppercase text-[#00247D] mr-auto" style={{ fontFamily: 'Barlow Condensed' }}>Who's available</h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Find official" className="pl-8 h-9 w-40" data-testid="availability-search" />
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => shift(-7)} data-testid="availability-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Input type="date" value={start} onChange={e => e.target.value && setStart(e.target.value)} className="h-9 w-40" data-testid="availability-start" />
          <Button variant="outline" size="sm" className="h-9" onClick={() => shift(7)} data-testid="availability-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="px-4 py-2.5 border-b"><Legend /></div>
        {!data ? <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : <Grid data={data} rows={rows} />}
      </section>
    </div>
  );
};
